import React, { useEffect, useImperativeHandle, useState } from 'react';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import { useForm } from 'react-hook-form';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormButton from '@app/component/form/button/FormButton';
import HotelRoomMappingType, { ROOM_MAPPING_TYPE_ORDER } from '@app/model/market_state/HotelRoomMappingType';
import FormSelect from '@app/component/form/input/select/FormSelect';
import _isEmpty from 'lodash/isEmpty';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import Message from '@app/component/message/Message';

import {
	useSaveCompanyHotelRoomMappingTypeConfigMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import RoomMappingTypeConfigurationOutput
	from '@app/model/company_hotel_room_mappings_type_configuration/RoomMappingTypeConfigurationOutput';

type MappingTypeOption = {
	name: string
	value: HotelRoomMappingType
}

const AddHotelRoomTypeModal: React.FunctionComponent<{
	ref: any,
	configs: RoomMappingTypeConfigurationOutput[]
}> = React.forwardRef(({ configs }, ref: React.Ref<any>) => {
	const modal = useModal();
	const form = useForm<{
		name: string,
		type: HotelRoomMappingType
	}>({
		defaultValues: {
			name: '',
			type: undefined,
		},
	});
	const [mappingTypeOptions, setMappingTypeOptions] = useState<MappingTypeOption[]>([]);
	const toaster = useToaster();
	const {companyHotel} = useSelectedCompanyHotel();

	useImperativeHandle(ref, () => {
		return {
			open: () => {
				modal.open();
				form.reset({
					name: '',
					type: undefined,
				});
			},
		};
	});

	useEffect(() => {
		if (!_isEmpty(configs)) {
			const options: MappingTypeOption[] = [];
			for (const c of configs) {
				if (!c.isEnabled && !c.isSystem) {
					createOption(options, c.type, 'SLOT_SINGLE_STANDARD_', 'After Single');
					createOption(options, c.type, 'SLOT_STANDARD_PREMIUM_', 'After Standard');
					createOption(options, c.type, 'SLOT_PREMIUM_SUITE_', 'After Premium');
					createOption(options, c.type, 'SLOT_SUITE_NONE_', 'After Suite');
				}
			}
			setMappingTypeOptions(options.sort((a, b) => ROOM_MAPPING_TYPE_ORDER.indexOf(a.value) - ROOM_MAPPING_TYPE_ORDER.indexOf(b.value)));
		}
	}, [configs]);

	const createOption = (options: MappingTypeOption[], type: HotelRoomMappingType, filter: string, name: string) => {
		if (type.startsWith(filter)) {
			if (!options.find(o => o.value.startsWith(filter)))
				options.push({
					name: name,
					value: type,
				});
		}
	};

	const saveCompanyHotelRoomMappingTypeMutation = useSaveCompanyHotelRoomMappingTypeConfigMutation({
		onSuccess: (result) => {
			modal.close();
			toaster.success('Add Room Type', 'Room type added successfully!');
		}
	});

	const onSubmit = (data: { name: string, type: HotelRoomMappingType }) => {
		saveCompanyHotelRoomMappingTypeMutation.mutate({ companyHotelId: companyHotel.id, name: data.name, type: data.type });
	};

	return <Modal ref={modal.ref} title="Add Room Type" actions={
		!_isEmpty(mappingTypeOptions) &&
		<FormButton state="primary" onClick={form.handleSubmit(onSubmit)}>Submit</FormButton>
	}>
		{_isEmpty(mappingTypeOptions) &&
			<Message state="warning" extraClassName="mb-4">You have reached the limit of types that can be
				added!</Message>}
		<div className="space-y-4">
			<FormInputText name="name" label="Name" form={form} isRequired placeholder="Insert name"
										 disabled={_isEmpty(mappingTypeOptions)} maxLength={30} />
			<FormSelect name="type" label="Set Position" form={form} isRequired placeholder="Select position" portal
									size="w-full"
									optionsSize="large"
									optionsTextSize="sm"
									disabled={_isEmpty(mappingTypeOptions)}
									options={mappingTypeOptions.map(o => {
										return { id: o.value, label: o.name, value: o.value };
									})} />
		</div>

	</Modal>;

});

export default AddHotelRoomTypeModal;